import MuiTooltip, {
  type TooltipProps as MuiTooltipProps,
} from '@mui/material/Tooltip';
import { useTooltipStyles } from './tooltip.styles';
import { TooltipPlain } from './plain';

import type { JSX } from 'react';

export type TooltipProps = Omit<MuiTooltipProps, 'content'> & {
  offset?: string | number;
  popoverFallbackPlacements?: MuiTooltipProps['placement'][];
  isDisabled?: boolean;
  isFaded?: boolean;
};

export const Tooltip = ({
  placement = 'top',
  offset,
  title,
  children,
  popoverFallbackPlacements = ['top', 'bottom', 'right', 'left'],
  isDisabled = false,
  isFaded = false,
  ...rest
}: TooltipProps): JSX.Element => {
  const styles = useTooltipStyles('dark');
  const { componentsProps: incoming, ...muiRest } = rest;

  const rawPopperSx = incoming?.popper?.sx;
  const callerPopperSx =
    rawPopperSx &&
    typeof rawPopperSx === 'object' &&
    !Array.isArray(rawPopperSx)
      ? rawPopperSx
      : {};

  return (
    <MuiTooltip
      {...muiRest}
      arrow
      placement={placement}
      enterDelay={200}
      enterTouchDelay={0}
      leaveDelay={50}
      leaveTouchDelay={50}
      title={
        isDisabled ? null : typeof title === 'string' && Boolean(title) ? (
          <TooltipPlain text={title} />
        ) : (
          title
        )
      }
      componentsProps={{
        ...incoming,
        popper: {
          ...incoming?.popper,
          sx: {
            ...callerPopperSx,
            ...(isFaded ? styles.popper : {}),
            ...styles.tooltip,
          },
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [
                  0,
                  offset
                    ? typeof offset === 'string'
                      ? Number.parseInt(offset)
                      : offset
                    : 0,
                ],
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: popoverFallbackPlacements,
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: typeof window !== 'undefined' && window,
                altAxis: true,
                tether: false,
              },
            },
          ],
        },
        tooltip: {
          ...incoming?.tooltip,
        },
      }}
    >
      {children}
    </MuiTooltip>
  );
};
